import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import sendMessage from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SendMessage";
import translate from "@insite/client-framework/Translate";
import { MessageModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { RfqQuoteDetailsPageContext } from "@insite/content-library/Pages/RfqQuoteDetailsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { blackBlueBtn } from "@tKgpco/Extensions/Components/WishListCardExtension";
import { getMessages, MessageCollectionModel } from "@tKgpco/Services/OrderDetailsMessageCollection";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadOrderDetailsMessages from "@tKgpco/Store/Data/OrderHistoryDetail/Handlers/LoadOrderMessages";
import sortBy from "lodash/sortBy";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    session: state.context.session,
    messageCollection: state.KGPState.LoadOrderMessagesStore.collection,
    currentBillTo: getBillToState(state, getSession(state).billToId).value,
});

const mapDispatchToProps = {
    sendMessage,
    loadOrderDetailsMessages,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderDetailsMessagesStyles {
    mainWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    messageTextArea?: TextAreaProps;
    sendButton?: ButtonPresentationProps;
    messagesContainer?: GridContainerProps;
    messageGridItem?: GridItemProps;
    userNameText?: TypographyPresentationProps;
    messageDateText?: TypographyPresentationProps;
    messageBodyText?: TypographyPresentationProps;
    viewAllMsg?: ClickablePresentationProps;
}

export const OrderDetailsMessagesStyles: OrderDetailsMessagesStyles = {
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            background-color: ${getColor("common.accent")};
            padding: 10px 20px;
            margin: 30px 0;
        `,
    },
    titleText: {
        variant: "h3",
    },
    messageTextArea: {
        cssOverrides: {
            formField: css`
                margin-bottom: 20px;
            `,
            inputSelect: css`
                width: 100%;
                resize: none;
            `,
        },
    },
    messagesContainer: {
        gap: 20,
        css: css`
            ${GridOffset} {
                margin-top: 20px;
            }
            ${GridItemStyle} {
                margin: 0 10px;
                padding: 10px 0;
                border-top: 1px solid ${getColor("common.border")};
            }
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    userNameText: {
        weight: 600,
        size: 15,
        css: css`
            text-transform: capitalize;
        `,
    },
    messageDateText: {
        size: 14,
    },
    messageBodyText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    viewAllMsg: {
        css: css`
            margin-top: 10px;
            font-weight: 400;
            color: ${getColor("primary")};
        `,
    },
};

const styles = OrderDetailsMessagesStyles;

const OrderDetailsMessages = ({
    quoteState,
    sendMessage,
    session,
    loadOrderDetailsMessages,
    messageCollection,
    currentBillTo,
}: Props) => {
    const quote = quoteState.value;
    const { value: order } = useContext(OrderStateContext);
    const [message, setMessage] = useState("");
    const [openMessageModal, setOpenMessageModal] = useState(false);

    const isOrderMessageEnabled = currentBillTo?.properties?.isOrderMessageEnabled?.toLowerCase() === "true";
    const isWorEnable = currentBillTo?.properties?.isWOREnable?.toLowerCase() === "true";
    const currentBillCustomerNumber = currentBillTo?.customerNumber;
    const orderBillCustomerNumber = order?.customerNumber;
    const isSameBillCustomerOrder = currentBillCustomerNumber === orderBillCustomerNumber;

    const messageChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.currentTarget.value);
    };

    useEffect(() => {
        if (order?.id) {
            loadOrderDetailsMessages({ orderHistoryId: order.id, size: 4 });
        }
    }, [order?.id]);

    const [sortedMessages, setSortedMessages] = useState<MessageCollectionModel[]>([]);
    useEffect(() => {
        if (!messageCollection?.length) {
            return;
        }

        setSortedMessages(sortBy(messageCollection || [], ["dateToDisplay"]).reverse());
    }, [messageCollection]);

    const sendClickHandler = () => {
        if (!message.trim() || !order) {
            return;
        }

        sendMessage({
            quote: {
                ...quote!,
                id: order.id,
                initiatedByUserName: session.userName || "",
                orderNumber: order.erpOrderNumber || order.webOrderNumber,
                properties: {
                    type: "Order",
                },
            },
            message,
        });
        setMessage("");
    };

    // if (!quote) {
    //     return null;
    // }

    const renderMessages = (sliceNum = sortedMessages.length) => {
        return sortedMessages.slice(0, sliceNum).map(message => (
            <GridItem key={message.dateToDisplay.toString()} {...styles.messageGridItem}>
                <Typography {...styles.userNameText}>{message.displayName}</Typography>
                <Typography {...styles.messageDateText}>
                    <LocalizedDateTime
                        dateTime={message.dateToDisplay as unknown as Date}
                        options={{
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                        }}
                    />
                </Typography>
                <Typography {...styles.messageBodyText}>{message.body}</Typography>
            </GridItem>
        ));
    };

    if (isSameBillCustomerOrder && isOrderMessageEnabled) {
        return (
            <StyledWrapper {...styles.mainWrapper}>
                <Typography {...styles.titleText}>{translate("Messages")}</Typography>
                <TextArea
                    {...styles.messageTextArea}
                    label={translate("Write a Message")}
                    placeholder={siteMessage("Rfq_GhostMessage") as string}
                    value={message}
                    onChange={messageChangeHandler}
                    data-test-selector="rfqQuoteDetails_MessageField"
                />
                <Button
                    // {...styles.sendButton}
                    {...blackBlueBtn.actionAddToCartButton}
                    disabled={!message.trim()}
                    onClick={sendClickHandler}
                    data-test-selector="rfqQuoteDetails_SendMessageButton"
                >
                    {translate("Send Message")}
                </Button>
                <GridContainer {...styles.messagesContainer} data-test-selector="rfqQuoteDetails_Messages">
                    {renderMessages(3)}
                    {sortedMessages.length > 3 && (
                        <GridItem {...styles.messageGridItem}>
                            <Clickable
                                {...styles.viewAllMsg}
                                onClick={() => {
                                    if (order?.id) {
                                        loadOrderDetailsMessages({ orderHistoryId: order?.id, size: 0 });
                                        setOpenMessageModal(true);
                                    }
                                }}
                            >
                                {translate("View All Messages...")}
                            </Clickable>
                        </GridItem>
                    )}
                </GridContainer>

                <Modal
                    headline={translate("Messages")}
                    isOpen={openMessageModal}
                    handleClose={() => setOpenMessageModal(false)}
                    sizeVariant="medium"
                >
                    {renderMessages()}
                </Modal>
            </StyledWrapper>
        );
    }

    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsMessages),
    definition: {
        displayName: "Order Details Messages",
        fieldDefinitions: [],
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
